// The SEPA step's two A/B tests (Paul, 2026-11-03), each switched per organisation:
//
//   sepa_dropin          = ab → half the patients get the embedded GoCardless Drop-in, half the redirect
//   sepa_optout_variant  = ab → half the patients get the two-step opt-out (reason → matching answer)
//
// The split is by patient number, so a patient always sees the same version: after a reload, on a
// second device, or coming back days later. Each test has its own salt, so the two splits are
// independent of each other. The arm a patient saw is stored on their submission (sepaVariant /
// sepaOptOutVariant); testers (test mode) are never counted and always see the new version.

export type SepaEntryVariant = 'dropin' | 'redirect'
export type SepaOptOutVariant = 'control' | 'two_step'

/** Why a patient opts out (two-step opt-out). Mirrored in server/src/routes/submissions.ts. */
export const SEPA_OPT_OUT_REASONS = ['privacy', 'control', 'invoice', 'other_payer', 'other'] as const
export type SepaOptOutReason = (typeof SEPA_OPT_OUT_REASONS)[number]

/**
 * 0 or 1 for this patient in this test. FNV-1a, then a full avalanche (murmur3's finaliser): plain
 * FNV's lowest bit is only the parity of the input, which would make sequential patient numbers
 * alternate and tie the two tests' splits together.
 */
export function abBucket(test: 'sepa_entry' | 'sepa_optout', patientNumber: string): 0 | 1 {
  const s = `${test}:${patientNumber}`
  let h = 0x811c9dc5
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  h ^= h >>> 16
  h = Math.imul(h, 0x85ebca6b)
  h ^= h >>> 13
  h = Math.imul(h, 0xc2b2ae35)
  h ^= h >>> 16
  return (h >>> 31) as 0 | 1
}

export interface SepaExperience {
  /** The embedded Drop-in (else the redirect to the mandate page) */
  dropin: boolean
  /** Ticking the opt-out asks for a reason and answers it */
  twoStepOptOut: boolean
  /** Only set while a 50/50 test runs for a real patient: the arm to store on the submission */
  sepaVariant: SepaEntryVariant | null
  sepaOptOutVariant: SepaOptOutVariant | null
}

export function sepaExperience(opts: {
  sepaDropin?: string | null
  sepaOptOutVariant?: string | null
  patientNumber: string
  testMode: boolean
}): SepaExperience {
  const { patientNumber, testMode } = opts
  const real = !testMode && /^P\d+$/.test(patientNumber)

  // sepa_dropin: unset = test (docs/sepa-dropin.md)
  const dropinMode = opts.sepaDropin || 'test'
  let dropin = dropinMode === 'on' || ((dropinMode === 'test' || dropinMode === 'ab') && testMode)
  let sepaVariant: SepaEntryVariant | null = null
  if (dropinMode === 'ab' && real) {
    sepaVariant = abBucket('sepa_entry', patientNumber) ? 'dropin' : 'redirect'
    dropin = sepaVariant === 'dropin'
  }

  // sepa_optout_variant: unset = off (the one-step box patients have always seen)
  const optOutMode = opts.sepaOptOutVariant || 'off'
  let twoStepOptOut = optOutMode === 'on' || ((optOutMode === 'test' || optOutMode === 'ab') && testMode)
  let sepaOptOutVariant: SepaOptOutVariant | null = null
  if (optOutMode === 'ab' && real) {
    sepaOptOutVariant = abBucket('sepa_optout', patientNumber) ? 'two_step' : 'control'
    twoStepOptOut = sepaOptOutVariant === 'two_step'
  }

  return { dropin, twoStepOptOut, sepaVariant, sepaOptOutVariant }
}
